import Reflux from 'reflux';
import { Notification, Notifications } from 'theme/types';

import { ThemeColorModes } from 'theme/colors';
import { Store } from 'stores/StoreTypes';
import fetch from 'logic/rest/FetchProvider';
import { singletonStore } from 'views/logic/singleton';
import { qualifyUrl } from 'util/URLUtils';
import UserNotification from 'util/UserNotification';
import { Status } from 'archive/types';

import CustomizationActions from './CustomizationActions';

type CustomizationStoreState = {
  status: Status,
  missing: boolean,
  loading: boolean,
};

const CustomizationStore: Store<CustomizationStoreState> = singletonStore('Customization', () => Reflux.createStore({
  listenables: [CustomizationActions],
  _state: {
    status: undefined,
    missing: false,
    loading: false,
  },

  getInitialState() {
    const { status, missing, loading } = this._state;

    return {
      status,
      missing,
      loading,
    };
  },

  updateTheme(theme: ThemeColorModes): Promise<void> {
    const url = qualifyUrl('/plugins/org.graylog.plugins.customization/theme');
    const promise = fetch('POST', url, theme);
    CustomizationActions.updateTheme.promise(promise);

    return promise;
  },

  loadTheme(): Promise<ThemeColorModes> {
    const url = qualifyUrl('/plugins/org.graylog.plugins.customization/theme');
    const promise = fetch('GET', url);

    CustomizationActions.loadTheme.promise(promise);

    return promise;
  },

  loadAllPublicNotifications(): Promise<Notifications> {
    const url = qualifyUrl('/plugins/org.graylog.plugins.customization/notifications');
    const promise = fetch('GET', url);

    CustomizationActions.loadAllPublicNotifications.promise(promise);

    return promise;
  },

  loadPublicNotification(id: string): Promise<Notification> {
    const url = qualifyUrl(`/plugins/org.graylog.plugins.customization/notifications/${id}`);
    const promise = fetch('GET', url);

    CustomizationActions.loadPublicNotification.promise(promise);

    return promise;
  },

  createPublicNotification(notification: Notification): Promise<Notification> {
    const url = qualifyUrl('/plugins/org.graylog.plugins.customization/notifications');
    const promise = fetch('POST', url, notification);
    CustomizationActions.createPublicNotification.promise(promise);

    return promise;
  },

  updatePublicNotification(id: string, notification: Notification): Promise<Notification> {
    const url = qualifyUrl(`/plugins/org.graylog.plugins.customization/notifications/${id}`);
    const promise = fetch('PUT', url, notification);
    CustomizationActions.updatePublicNotification.promise(promise);

    return promise;
  },

  deletePublicNotification(id: string): Promise<0 | 1> {
    const url = qualifyUrl(`/plugins/org.graylog.plugins.customization/notifications/${id}`);
    const promise = fetch('DELETE', url);

    CustomizationActions.deletePublicNotification.promise(promise);

    return promise;
  },

  getLicenseStatus() {
    const url = qualifyUrl('/plugins/org.graylog.plugins.license/licenses/status/for-subject?subject=/license/enterprise/customization');
    const promise = fetch('GET', url);
    const errorHandler = this._errorHandler('Fetching customization license failed', 'Could not retrieve customization'
      + ' license');

    promise.then((response) => {
      this._updateState({ status: response.status, missing: false, loading: false });
      this.trigger({ status: response.status, missing: false, loading: false });
    }, (error) => {
      try {
        // We don't want to create a user notification for missing licenses
        if (error.additional.status !== 404) {
          errorHandler(error);
        }
      } catch (e) {
        errorHandler(error);
      }

      this._updateState({ status: null, missing: true, loading: false });
      this.trigger({ status: null, missing: true, loading: false });
    });

    CustomizationActions.getLicenseStatus.promise(promise);
  },

  _errorHandler(message, title) {
    return (error) => {
      let errorMessage;

      try {
        errorMessage = error.additional.body.message;
      } catch (e) {
        errorMessage = error.message;
      }

      UserNotification.error(`${message}: ${errorMessage}`, title);
    };
  },

  _updateState({ status, missing, loading }) {
    this._state = {
      status,
      missing,
      loading,
    };
  },
}));

export { CustomizationActions, CustomizationStore };
export default CustomizationStore;
